import { useCallback, useMemo } from "react";
import { APP_NAME, shareMatchInviteText } from "../../../../config/brand";
import {
  buildShareableMatchUrl,
  buildRosterJoinUrl,
  buildTvModeUrl,
  copyMatchUrlToClipboard,
  shareMatchUrl,
} from "../../../../services/session/matchShare";
import {
  downloadMatchQrPng,
  matchQrDownloadFilename,
} from "../../../../services/session/matchQr";

/**
 * Shareable match URL + copy / Web Share / QR PNG download for the current room.
 * Pass `inviteUrl` (Arena /join/:id) to prefer frictionless invite over spectator ?room= links.
 */
export function useMatchRoomShare(roomCode, { inviteUrl = "" } = {}) {
  const legacyShareUrl = useMemo(
    () => buildShareableMatchUrl(roomCode),
    [roomCode]
  );
  const shareUrl = useMemo(() => {
    const preferred = String(inviteUrl || "").trim();
    return preferred || legacyShareUrl;
  }, [inviteUrl, legacyShareUrl]);
  const rosterJoinUrl = useMemo(() => {
    const preferred = String(inviteUrl || "").trim();
    return preferred || buildRosterJoinUrl(roomCode);
  }, [inviteUrl, roomCode]);
  const tvModeUrl = useMemo(() => buildTvModeUrl(roomCode), [roomCode]);

  const share = useCallback(async (options = {}) => {
    const result = await shareMatchUrl({
      url: shareUrl,
      title: options.title || `${APP_NAME} match`,
      text: options.text || shareMatchInviteText(),
      copyText: options.copyText,
    });
    if (result === "shared") return "Link shared";
    if (result === "copied") return "Link copied to clipboard";
    return null;
  }, [shareUrl]);

  const copyLink = useCallback(async () => {
    if (!shareUrl) return "Invite link not ready yet";
    const result = await copyMatchUrlToClipboard(shareUrl);
    if (result === "copied") return "Link copied to clipboard";
    return "Could not copy link";
  }, [shareUrl]);

  const downloadQr = useCallback(
    (canvas) => {
      const ok = downloadMatchQrPng(
        canvas,
        matchQrDownloadFilename(roomCode),
        "spectator"
      );
      return ok ? "QR code downloaded" : "Could not download QR code";
    },
    [roomCode]
  );

  const copyTvLink = useCallback(async () => {
    const result = await copyMatchUrlToClipboard(tvModeUrl);
    if (result === "copied") return "TV link copied to clipboard";
    return "Could not copy link";
  }, [tvModeUrl]);

  const downloadTvQr = useCallback(
    (canvas) => {
      const ok = downloadMatchQrPng(
        canvas,
        matchQrDownloadFilename(roomCode, "tv")
      );
      return ok ? "QR code downloaded" : "Could not download QR code";
    },
    [roomCode]
  );

  const copyRosterLink = useCallback(async () => {
    const result = await copyMatchUrlToClipboard(rosterJoinUrl);
    if (result === "copied") return "Invite link copied";
    return "Could not copy link";
  }, [rosterJoinUrl]);

  const downloadRosterQr = useCallback(
    (canvas) => {
      const ok = downloadMatchQrPng(
        canvas,
        matchQrDownloadFilename(roomCode, "invite"),
        "roster"
      );
      return ok ? "QR code downloaded" : "Could not download QR code";
    },
    [roomCode]
  );

  return {
    shareUrl,
    rosterJoinUrl,
    tvModeUrl,
    share,
    copyLink,
    copyTvLink,
    copyRosterLink,
    downloadQr,
    downloadTvQr,
    downloadRosterQr,
  };
}
